import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { Link } from 'react-router-dom';
import { trackEvent } from '../services/analytics';

// Response tone moved to Settings along with business type, description and the
// sign-off name. They shape the AI's voice, not the number a new user came for.

const detectCurrency = () => {
  try {
    const locale = navigator.language || 'en-GB';
    if (locale.includes('US') || locale === 'en-US') return 'usd';
    return 'gbp';
  } catch {
    return 'gbp';
  }
};

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessCategory, setBusinessCategory] = useState('local_business');
  const [businessType, setBusinessType] = useState('');
  const [businessDescription, setBusinessDescription] = useState('');
  const [ownerFirstName, setOwnerFirstName] = useState('');
  const [responseTone, setResponseTone] = useState('friendly, casual');
  const [currency, setCurrency] = useState(detectCurrency());
  const [signupRating, setSignupRating] = useState('');
  const [signupReviews, setSignupReviews] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Auto-open signup form if ?register=true in URL; ?category=hospitality
  // preselects the hospitality chip so Airbnb-tool visitors don't land on
  // the Local Business default
  useEffect(() => {
    if (searchParams.get('register') === 'true') {
      setIsRegister(true);
    }
    if (searchParams.get('category') === 'hospitality') {
      setBusinessCategory('hospitality');
    }
  }, [searchParams]);

  const isHospitality = businessCategory === 'hospitality';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isRegister) {
        await api.register(email, password, fullName, businessName, {
          businessType: businessType || null,
          businessDescription: businessDescription || null,
          businessCategory,
          ownerFirstName: ownerFirstName || null,
          responseTone,
          currency,
          selfReportedRating: signupRating ? parseFloat(signupRating) : null,
          selfReportedReviews: signupReviews ? parseInt(signupReviews, 10) : null,
        });
        trackEvent('signup_completed', { category: businessCategory, currency });
      } else {
        await api.login(email, password);
        trackEvent('login');
      }
      navigate('/');
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card card">
        <p style={{ marginBottom: '12px', fontSize: '13px' }}>
          <Link to="/" style={{ color: 'var(--text-light)', textDecoration: 'none' }}>
            &larr; Back to home
          </Link>
        </p>
        <h1 className="login-title">
          <Link to="/" style={{ color: 'inherit', textDecoration: 'none' }}>
            Stellar<span className="login-title-accent">Reply</span>
          </Link>
        </h1>
        <p className="login-subtitle">
          {isRegister
            ? 'See exactly where your rating stands, in under a minute'
            : 'AI-powered responses to every review'}
        </p>

        <form onSubmit={handleSubmit}>
          {isRegister && (
            <>
              <div className="form-group">
                <label className="form-label">What type of business?</label>
                <div className="category-selector">
                  <button
                    type="button"
                    className={`category-chip ${businessCategory === 'local_business' ? 'active' : ''}`}
                    onClick={() => setBusinessCategory('local_business')}
                  >
                    <span className="category-icon">🏪</span>
                    <span className="category-label">Local Business</span>
                    <span className="category-hint">Restaurants, salons, shops, trades</span>
                  </button>
                  <button
                    type="button"
                    className={`category-chip ${businessCategory === 'hospitality' ? 'active' : ''}`}
                    onClick={() => setBusinessCategory('hospitality')}
                  >
                    <span className="category-icon">🏠</span>
                    <span className="category-label">Hospitality</span>
                    <span className="category-hint">Airbnb, holiday lets, B&amp;Bs</span>
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input
                  className="form-input"
                  type="text"
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  placeholder="Your name"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  {isHospitality ? 'Property Name' : 'Business Name'}
                </label>
                <input
                  className="form-input"
                  type="text"
                  value={businessName}
                  onChange={e => setBusinessName(e.target.value)}
                  placeholder={isHospitality
                    ? 'e.g. Cosy Bromley Flat'
                    : 'e.g. London Coffee Shop'}
                  required
                />
              </div>

              {/* Business type, description, sign-off name and tone all moved to
                  Settings. They only shape the AI's voice, and asking for them here
                  put five fields between signing up and seeing a number. The Get
                  Started card on the dashboard links straight to them. */}
              <div className="form-group">
                <label className="form-label">
                  Where you stand{' '}
                  <span style={{ fontWeight: 'normal', color: 'var(--text-light)' }}>(optional)</span>
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    className="form-input"
                    type="number"
                    inputMode="decimal"
                    min="1"
                    max="5"
                    step="0.01"
                    value={signupRating}
                    onChange={e => setSignupRating(e.target.value)}
                    placeholder={isHospitality ? 'Rating e.g. 4.76' : 'Rating e.g. 4.3'}
                  />
                  <input
                    className="form-input"
                    type="number"
                    inputMode="numeric"
                    min="1"
                    step="1"
                    value={signupReviews}
                    onChange={e => setSignupReviews(e.target.value)}
                    placeholder="Reviews e.g. 38"
                  />
                </div>
                <span className="form-hint">
                  {isHospitality
                    ? 'Your listing rating and review count. We will show you exactly how far you are from 4.8 Superhost the moment you are in.'
                    : 'Your current rating and review count. We will show you exactly how many 5-star reviews you need the moment you are in.'}
                </span>
              </div>

              <div className="form-group">
                <label className="form-label">Pricing currency</label>
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                  <button
                    type="button"
                    onClick={() => setCurrency('gbp')}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: currency === 'gbp' ? '2px solid var(--accent)' : '1px solid var(--border)',
                      background: currency === 'gbp' ? 'var(--green-light, #ecfdf5)' : 'transparent',
                      cursor: 'pointer',
                      fontWeight: currency === 'gbp' ? '600' : '400',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      color: 'var(--text)',
                    }}
                  >
                    🇬🇧 GBP (£)
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrency('usd')}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: currency === 'usd' ? '2px solid var(--accent)' : '1px solid var(--border)',
                      background: currency === 'usd' ? 'var(--green-light, #ecfdf5)' : 'transparent',
                      cursor: 'pointer',
                      fontWeight: currency === 'usd' ? '600' : '400',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      color: 'var(--text)',
                    }}
                  >
                    🇺🇸 USD ($)
                  </button>
                </div>
                <span className="form-hint">This sets your subscription currency and cannot be changed later.</span>
              </div>
            </>
          )}

          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              className="form-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@business.com"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <input
              className="form-input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>

          {error && <div className="form-error">{error}</div>}

          <button className="btn btn-primary login-btn" type="submit" disabled={loading}>
            {loading ? 'Please wait...' : (isRegister ? 'Create Account' : 'Sign In')}
          </button>
          <p style={{ textAlign: 'center', marginTop: '16px', fontSize: '14px' }}>
            <Link to="/forgot-password" style={{ color: 'var(--primary)', textDecoration: 'none' }}>
              Forgot your password?
            </Link>
          </p>
        </form>

        <div className="login-switch">
          {isRegister ? (
            <>Already have an account? <a href="#" onClick={(e) => { e.preventDefault(); setIsRegister(false); }}>Sign in</a></>
          ) : (
            <>Don't have an account? <a href="#" onClick={(e) => { e.preventDefault(); setIsRegister(true); }}>Start your free trial</a></>
          )}
        </div>

        {!isRegister && (
          <div className="login-features">
            <div className="login-feature">
              <span className="login-feature-icon">⚡</span>
              <span>Respond to reviews in seconds, not hours</span>
            </div>
            <div className="login-feature">
              <span className="login-feature-icon">🤖</span>
              <span>AI drafts that match your brand voice</span>
            </div>
            <div className="login-feature">
              <span className="login-feature-icon">🌍</span>
              <span>Built-in translation for reviews in any language</span>
            </div>
          </div>
        )}

        <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '13px', color: 'var(--text-light)' }}>
          Need help?{' '}
          <a href="mailto:admin@opslife.co.uk" style={{ color: 'var(--primary)', textDecoration: 'none' }}>
            admin@opslife.co.uk
          </a>
        </p>
      </div>
    </div>
  );
}
